import React, { useState } from "react";
import {
  Card,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { createEmployee } from "../../services/employeeService";
import MuiAlert, { AlertProps } from "@mui/material/Alert";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddEmployee: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    empCode: "",
    employeeType: "",
    ctc: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [confirmOpen, setConfirmOpen] = useState(false);

  //Handle Changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "firstName" || name === "lastName") {
      newValue = newValue.replace(/[^a-zA-Z ]/g, "");
    }
    if (name === "ctc") {
      newValue = newValue.replace(/[^0-9]/g, "");
    }
    if (name === "empCode") {
      newValue = newValue.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    }
    setForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  // Handle Save
  const handleSave = () => {
    setConfirmOpen(true);
  };

  // Handle Confirm
  const confirmSave = async () => {
    setConfirmOpen(false);
    const now = new Date().toISOString();
    const payload = {
      createdOn: now,
      createdBy: 1,
      modifiedOn: now,
      modifiedBy: 1,
      isDeleted: false,
      isActive: true,
      employeeId: 0,
      firstName: form.firstName,
      lastName: form.lastName,
      empCode: form.empCode,
      employeeType: form.employeeType,
      ctc: parseFloat(form.ctc),
    };
    try {
      const res = await createEmployee(payload);
      setSnackbar({ open: true, message: "Employee Created Successfully", severity: "success" });
      setTimeout(() => {
        navigate("/EmployeeTable");
      }, 1500);
    } catch (error) {
      console.error("Error creating employee:", error);
      setSnackbar({ open: true, message: "Failed to create employee.", severity: "error" });
    }

  };

  // Fields for Search Drawer
  const fields = [
    { name: "empCode", label: "Employee Code", type: "text" },
    { name: "firstName", label: "First Name", type: "text" },
    { name: "lastName", label: "Last Name", type: "text" },
    { name: "employeeType", label: "Employee Type", type: "dropdown", options: ["Full-time", "Part-time", "Consultant"] },
    { name: "ctc", label: "CTC (₹)", type: "text" },
  ];

  return (
    <Box sx={{ bgcolor: "#f0f2f5", p: 3, minHeight: "100vh" }}>
      <Card elevation={1} sx={{ bgcolor: "#fff", m: 2, p: 3, minHeight: "80vh", width: "calc(100% - 32px)" }}>
        <Typography variant="h6" mb={3}>Add Employee</Typography>
        <Grid container spacing={2} maxWidth="600px" direction="column">
          {fields.map(({ name, label, type, options }) => (
            <Grid item key={name}>
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Typography sx={{ width: "200px", textAlign: "right", pr: 1 }}>{label}</Typography>
                {type === "dropdown" ? (
                  <TextField
                    select
                    name={name}
                    value={form[name as keyof typeof form]}
                    onChange={handleChange}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    {options?.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    name={name}
                    type={type}
                    value={form[name as keyof typeof form]}
                    onChange={handleChange}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box mt={10} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="contained" color="primary" onClick={handleSave}>SAVE</Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate("/EmployeeTable")}>BACK</Button>
        </Box>
      </Card>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>Are you sure you want to add this employee?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmSave} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddEmployee;
